import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import { Lockup } from "@/components/Mark";

/**
 * 2026-10-27 — the top bar, in the app's language: the mark and the wordmark on the left,
 * a few plain words in the middle, one white button on the right. On a phone the menu is a
 * drawer from the left, as in the app. (The old bar's gear held a "Coming" list and the
 * Catalog dropdown sizes in GB — both gone; the Experts page is the catalog.)
 */
const NAV = [
  { to: "/experts", label: "Experts" },
  { to: "/how-it-works", label: "How it works" },
  { to: "/train", label: "Train" },
  { to: "/app", label: "Download" },
  { to: "/about", label: "About" },
];

export default function TopBar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <>
      <header data-testid="top-bar" className={`ex-topbar ${scrolled ? "is-scrolled" : ""}`}>
        <div className="ex-wrap h-full flex items-center gap-4">
          <button
            type="button" onClick={() => setOpen(true)} aria-label="Open the menu"
            className="md:hidden -ml-2 p-2 rounded-full text-[color:var(--ex-text2)] hover:text-white"
            data-testid="nav-mobile-toggle"
          >
            <Menu className="h-5 w-5" strokeWidth={1.7} />
          </button>
          <Link to="/" data-testid="brand" aria-label="Experts — home" className="shrink-0">
            <Lockup size={22} />
          </Link>
          <nav className="hidden md:flex items-center gap-1 mx-auto" aria-label="Main">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} className={({ isActive }) => `ex-navlink ${isActive ? "is-active" : ""}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto md:ml-0 flex items-center gap-2">
            <NavLink to="/library" className={({ isActive }) => `hidden sm:inline-flex ex-navlink ${isActive ? "is-active" : ""}`}>
              My library
            </NavLink>
            <Link to="/app" className="ex-btn ex-btn-primary ex-btn-sm" data-testid="topbar-download">
              Get the app
            </Link>
          </div>
        </div>
      </header>

      {open && (
        <>
          <div className="ex-drawer-scrim" onClick={() => setOpen(false)} />
          <aside className="ex-drawer" data-testid="nav-mobile-drawer" aria-label="Menu">
            <div className="flex items-center justify-between px-2 pb-4">
              <Lockup size={22} />
              <button type="button" onClick={() => setOpen(false)} aria-label="Close the menu" className="p-2 rounded-full text-[color:var(--ex-text2)] hover:text-white">
                <X className="h-5 w-5" strokeWidth={1.7} />
              </button>
            </div>
            {[{ to: "/", label: "Home" }, ...NAV, { to: "/library", label: "My library" }].map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className={({ isActive }) => `ex-drawer-link ${isActive ? "is-active" : ""}`}>
                <span>{n.label}</span>
                <ArrowRight className="h-4 w-4 opacity-40" strokeWidth={1.7} />
              </NavLink>
            ))}
            <div className="mt-auto px-3 pt-6 ex-tiny">
              No account. No tracking. Everything runs on your phone.
            </div>
          </aside>
        </>
      )}
    </>
  );
}
